import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  KeyRound,
  MoreHorizontal,
  ShieldCheck,
  Trash2,
  UserCog,
  UserPlus,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { usersApi, USER_ROLES, type UserRole, type UserSummary } from "@/api/users";
import { queryKeys } from "@/api/query-keys";
import { authErrorMessage } from "@/auth/errors";
import { requireRole } from "@/auth/role-guard";
import { useAuth } from "@/auth/auth-provider";
import { formatRelative } from "@/lib/format";
import { CreateUserDialog } from "./-dialogs/create-user-dialog";

export const Route = createFileRoute("/_authenticated/usuarios/")({
  beforeLoad: requireRole(["admin"]),
  component: UsuariosPage,
});

function RoleBadge({ role }: { role: UserRole | null }) {
  const { t } = useTranslation();
  if (!role) return <Badge variant="outline">{t("users.roles.none")}</Badge>;
  const variant =
    role === "admin" ? "default" : role === "inscripcion" ? "secondary" : "outline";
  return <Badge variant={variant}>{t(`users.roles.${role}`)}</Badge>;
}

function StatusBadge({ user }: { user: UserSummary }) {
  const { t } = useTranslation();
  if (!user.enabled)
    return <Badge variant="destructive">{t("users.status.disabled")}</Badge>;
  switch (user.status) {
    case "CONFIRMED":
      return <Badge variant="success">{t("users.status.active")}</Badge>;
    case "FORCE_CHANGE_PASSWORD":
      return (
        <Badge variant="warning">{t("users.status.mustChangePassword")}</Badge>
      );
    case "RESET_REQUIRED":
      return <Badge variant="warning">{t("users.status.resetRequired")}</Badge>;
    default:
      return <Badge variant="secondary">{user.status}</Badge>;
  }
}

function UsuariosPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<UserSummary | null>(null);

  const listQuery = useQuery({
    queryKey: queryKeys.users.list,
    queryFn: usersApi.list,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.users.all });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: UserRole; enabled?: boolean }) =>
      usersApi.update(id, body),
    onSuccess: (_r, vars) => {
      invalidate();
      toast.success(
        vars.enabled === false
          ? t("users.toast.disabled")
          : vars.enabled === true
            ? t("users.toast.enabled")
            : t("users.toast.roleChanged"),
      );
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const resetMutation = useMutation({
    mutationFn: (id: string) => usersApi.resetPassword(id),
    onSuccess: (res) =>
      toast.success(
        res.method === "resend" ? t("users.toast.resent") : t("users.toast.reset"),
      ),
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("users.toast.deleted"));
      setToDelete(null);
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const busy =
    updateMutation.isPending || resetMutation.isPending || deleteMutation.isPending;

  const columns: Column<UserSummary>[] = [
    {
      id: "user",
      header: t("users.columns.user"),
      cell: (u) => (
        <div>
          <p className="font-medium">{u.email}</p>
          {u.name ? (
            <p className="text-xs text-muted-foreground">{u.name}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: "role",
      header: t("users.columns.role"),
      cell: (u) => <RoleBadge role={u.role} />,
    },
    {
      id: "status",
      header: t("users.columns.status"),
      cell: (u) => <StatusBadge user={u} />,
    },
    {
      id: "created",
      header: t("users.columns.created"),
      cell: (u) => (
        <span className="text-sm text-muted-foreground">
          {formatRelative(u.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      headClassName: "w-12",
      className: "text-right",
      cell: (u) => {
        const isSelf = currentUser?.username === u.id;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                aria-label={t("users.actions.menu")}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{t("users.actions.changeRoleTo")}</DropdownMenuLabel>
              {USER_ROLES.map((r) => (
                <DropdownMenuItem
                  key={r}
                  disabled={u.role === r || (isSelf && r !== "admin")}
                  onClick={() => updateMutation.mutate({ id: u.id, role: r })}
                >
                  <ShieldCheck className="size-4" />
                  {t(`users.roles.${r}`)}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => resetMutation.mutate(u.id)}
              >
                <KeyRound className="size-4" />
                {t("users.actions.resetPassword")}
              </DropdownMenuItem>
              {u.enabled ? (
                <DropdownMenuItem
                  disabled={isSelf}
                  onClick={() =>
                    updateMutation.mutate({ id: u.id, enabled: false })
                  }
                >
                  <UserCog className="size-4" />
                  {t("users.actions.disable")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() =>
                    updateMutation.mutate({ id: u.id, enabled: true })
                  }
                >
                  <UserCog className="size-4" />
                  {t("users.actions.enable")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={isSelf}
                className="text-destructive focus:text-destructive"
                onClick={() => setToDelete(u)}
              >
                <Trash2 className="size-4" />
                {t("users.actions.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <UserPlus className="size-4" />
            {t("users.actions.create")}
          </Button>
        }
      />

      <DataTable<UserSummary>
        data={listQuery.data ?? []}
        columns={columns}
        getRowId={(u) => u.id}
        isLoading={listQuery.isLoading}
        isFetching={listQuery.isFetching}
        error={listQuery.error}
        emptyState={
          <EmptyState
            icon={UsersIcon}
            title={t("users.empty.title")}
            description={t("users.empty.description")}
            actions={
              <Button onClick={() => setCreateOpen(true)}>
                <UserPlus className="size-4" />
                {t("users.actions.create")}
              </Button>
            }
          />
        }
      />

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("users.delete.title")}</DialogTitle>
            <DialogDescription>
              {t("users.delete.description", { email: toDelete?.email ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setToDelete(null)}
              disabled={deleteMutation.isPending}
            >
              {t("common.actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => toDelete && deleteMutation.mutate(toDelete.id)}
            >
              <Trash2 className="size-4" />
              {t("users.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
